<template>
  <messenger-view-layout
    :title="viewSettings.channel.name"
    :channel-type="WebWidgetChannelType.EMAIL"
    :scroll-bar-id="scrollBarId"
    @back="$emit('back')"
    @resize="$emit('resize', $event)"
  >
    <template v-slot:alert>
      <slot name="alert" />
    </template>

    <div class="view-content">
      <messenger-view-content-header
        class="view-content__header"
        :title="viewSettings.title"
        :description="viewSettings.description"
      />

      <div class="view-content__fields view-fields">
        <widget-form-line
          :label="$t('messengerWidget.nameField.label')"
          :required="fieldMap['name'].isRequired"
          :style="{ order: fieldMap['name'].order }"
        >
          <widget-form-field-text
            name="name"
            :placeholder="$t('messengerWidget.nameField.placeholder')"
            :tabindex="fieldMap['name'].order"
          />
        </widget-form-line>

        <widget-form-line
          :label="$t('messengerWidget.emailField.label')"
          :required="fieldMap['email'].isRequired"
          :style="{ order: fieldMap['email'].order }"
        >
          <widget-form-field-text
            name="email"
            :placeholder="$t('messengerWidget.emailField.placeholder')"
            :tabindex="fieldMap['email'].order"
          />
        </widget-form-line>

        <widget-form-line
          :label="$t('messengerWidget.messageField.label')"
          :required="fieldMap['message'].isRequired"
          :style="{ order: fieldMap['message'].order }"
        >
          <widget-form-field-textarea
            name="message"
            :placeholder="$t('messengerWidget.messageField.placeholder')"
            size="100px"
            :tabindex="fieldMap['message'].order"
          />
        </widget-form-line>

        <widget-form-line
          :label="$t('messengerWidget.attachmentsField.label')"
          :style="{ order: fieldMap['message'].order }"
        >
          <widget-file-picker
            v-if="attachments.length < messengerMaxFilesCountPerMessage"
            :accept="messengerAcceptedFileExtensions"
            :max-files-count="messengerMaxFilesCountPerMessage"
            @files-picked="$emit('files-picked', $event)"
          />
          <widget-field-attachment-list
            v-if="attachments.length"
            class="mt-3"
            :attachments="attachments"
            @remove-attachment="$emit('remove-attachment', $event)"
            @retry-attachment="$emit('retry-attachment', $event)"
          />
        </widget-form-line>
      </div>
    </div>

    <template v-slot:footer-content>
      <div class="view-footer">
        <widget-button
          v-bind="themeButtonProps"
          class="w100pr"
          :is-loading="isLoading"
          :loading-title="$t('common.sending')"
          @click="$emit('submit')"
        >
          <messenger-text-with-emoji :text="viewSettings.buttonText" />
        </widget-button>
      </div>
    </template>
  </messenger-view-layout>
</template>

<script setup lang="ts">
import type { MessengerEmailViewSettings } from '@widgets/messenger/types'
import MessengerViewLayout from '@widgets/messenger/components/MessengerViewLayout.vue'
import WidgetFormLine from '@widgets/shared/components/WidgetFormLine.vue'
import WidgetFormFieldText from '@widgets/shared/components/fields/WidgetFormFieldText.vue'
import WidgetButton from '@widgets/shared/components/WidgetButton.vue'
import WidgetFormFieldTextarea from '@widgets/shared/components/fields/WidgetFormFieldTextarea.vue'
import WidgetFilePicker from '@widgets/shared/components/WidgetFilePicker.vue'
import { useFormFieldMap } from '@widgets/messenger/compositions/useFormFieldMap'
import MessengerViewContentHeader from '@widgets/messenger/components/MessengerViewContentHeader.vue'
import MessengerTextWithEmoji from '@widgets/messenger/components/MessengerTextWithEmoji.vue'
import { useWidgetTheme } from '@widgets/messenger/compositions/useWidgetTheme'
import { messengerAcceptedFileExtensions, messengerMaxFilesCountPerMessage } from '@widgets/messenger/const'
import {
  isWidgetUploadingAttachment,
  type WidgetAttachment,
  type WidgetUploadingAttachmentError,
} from '@widgets/shared/types'
import WidgetFieldAttachmentList from '@widgets/shared/components/attachment/field/WidgetFieldAttachmentList.vue'
import scrollbarService from '@widgets/shared/services/scrollbarService'
import { watch, nextTick, computed } from '@/composition/vue/compositionApi'
import {
  WebWidgetChannelType,
  type WebWidgetDetailsEmailFieldName,
} from '@/data/models/domain/messages/settings/webWidget/types'

const {
  viewSettings,
  attachments = [],
  scrollBarId,
} = defineProps<{
  viewSettings: MessengerEmailViewSettings
  isLoading?: boolean
  attachments?: WidgetAttachment[]
  scrollBarId?: string
}>()

defineEmits<{
  (e: 'submit'): void
  (e: 'back'): void
  (e: 'files-picked', files: File[]): void
  (e: 'remove-attachment', attachmentId: string): void
  (e: 'retry-attachment', attachmentId: string): void
  (e: 'resize', height: number): void
}>()

const { themeButtonProps } = useWidgetTheme()

const { fieldMap } = useFormFieldMap<WebWidgetDetailsEmailFieldName>(() => viewSettings.formFields)

const scrollToBottom = async () => {
  if (!scrollBarId) {
    return
  }

  await nextTick()

  try {
    scrollbarService.scrollToBottom(scrollBarId)
  } catch (e) {
    // Silent fail
  }
}

// Scroll to bottom when new attachment is added
watch(
  () => attachments.length,
  (newLength, oldLength) => {
    if (newLength > oldLength) {
      scrollToBottom()
    }
  },
)

const attachmentUploadErrorList = computed<Array<WidgetUploadingAttachmentError>>(() => {
  return attachments.filter(isWidgetUploadingAttachment).map((att) => att.error!)
})

const handleAttachmentUploadErrors = (
  newErrors: Array<WidgetUploadingAttachmentError>,
  oldErrors: Array<WidgetUploadingAttachmentError>,
) => {
  for (let i = 0; i < newErrors.length; i++) {
    if (newErrors[i] && !oldErrors[i]) {
      scrollToBottom()
      break
    }
  }
}

watch(attachmentUploadErrorList, handleAttachmentUploadErrors, { deep: true })
</script>

<style lang="scss" scoped>
.view-content {
  padding: 40px 24px 0;

  &__header {
    margin-bottom: 24px;
  }
}

.view-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.view-footer {
  padding: 24px 24px 0;
}
</style>
